import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, ChevronRight, Globe, PanelLeftClose } from 'lucide-react';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { NotificationBell } from '../../components/admin/NotificationBell';
import logoImage from '../../assets/images/logo.webp';
interface AdminTopBarProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  user: { firstName: string; lastName: string; email: string } | null;
  unreadCount: number;
  notificationsList: {
    id: number;
    type: 'member' | 'donation' | 'contact' | 'event' | 'system';
    title: string;
    message: string;
    createdAt: string;
    read: boolean;
  }[];
  notificationsOpen: boolean;
  setNotificationsOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
  markAsRead: { mutate: (id: number) => void };
  markAllAsRead: { mutate: () => void };
}

export function AdminTopBar({
  sidebarCollapsed,
  setSidebarCollapsed,
  setMobileMenuOpen,
  user,
  unreadCount,
  notificationsList,
  notificationsOpen,
  setNotificationsOpen,
  markAsRead,
  markAllAsRead,
}: AdminTopBarProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16">
        <div className="flex items-center justify-between px-3 h-full">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-xl text-slate-600 hover:bg-slate-100 transition-all duration-200"
            aria-label={t('admin.layout.openMenu')}
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to="/admin" className="flex items-center gap-2">
            <img
              src={logoImage}
              alt="Logo YIM"
              width={32}
              height={32}
              loading="eager"
              decoding="async"
              className="w-8 h-8 object-contain"
            />
            <span className="font-heading font-bold text-slate-900">{t('admin.title')}</span>
          </Link>

          <div className="flex items-center gap-1">
            <LanguageSwitcher variant="toggle" showFlags={false} />
            <Link
              to="/"
              className="p-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-all duration-200"
              aria-label={t('admin.topbar.viewWebsite')}
              title={t('admin.topbar.viewWebsite')}
            >
              <Globe className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Desktop Top Bar */}
      <div className="hidden lg:flex fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50">
        <div className={`flex items-center justify-between px-4 border-r border-slate-200 transition-all duration-300 ${sidebarCollapsed ? 'w-[72px]' : 'w-64'}`}>
          <Link
            to="/admin"
            className={`flex items-center gap-3 ${sidebarCollapsed ? 'justify-center w-full' : ''}`}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E40AF] to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <img
                src={logoImage}
                alt="Logo YIM"
                width={24}
                height={24}
                loading="eager"
                decoding="async"
                className="w-6 h-6 object-contain"
              />
            </div>
            {!sidebarCollapsed && (
              <span className="font-heading font-bold text-slate-900 text-lg tracking-tight">
                YIM
              </span>
            )}
          </Link>
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1.5 rounded-lg text-slate-600 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200"
              title={t('admin.layout.collapseSidebar')}
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex-1 flex items-center justify-between px-6">
          <div className="flex items-center gap-3" data-tour="topbar-welcome">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-slate-600">
              {t('admin.topbar.welcomeBack')},{' '}
              <span className="font-semibold text-slate-900">
                {user?.firstName}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div data-tour="notifications">
              <NotificationBell
                unreadCount={unreadCount}
                notifications={notificationsList}
                onMarkRead={(id) => markAsRead.mutate(id)}
                onMarkAllRead={() => markAllAsRead.mutate()}
                onNotificationClick={() => setNotificationsOpen(false)}
                isOpen={notificationsOpen}
                onToggle={() => setNotificationsOpen((prev: boolean) => !prev)}
              />
            </div>
            <LanguageSwitcher variant="toggle" showFlags={true} />
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition-all duration-200 shadow-sm"
            >
              {t('admin.topbar.viewWebsite')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
